import {
  Alert,
  Autocomplete,
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { AssetSubType } from "../../../Core/Api/Types/asset-sub-type";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AssetIcon from "../../Misc/AssetIcon/asset-icon";
import { TranskodeBundleComponent } from "../../../Core/Api/Types/transkode-bundle-component";
import { TranskodeBundleComponentType } from "../../../Core/Api/Types/transkode-bundle-component-type";
import { XmlBundleDeliveryIdentityProfile } from "../../../Core/Api/Types/xml-bundle-delivery-identity-profile";
import { XmlTechAudioInfos } from "../../../Core/Api/Types/xml-tech-audio-infos";
import { GetAudioLabel } from "../../../Utils/Helpers/IntelliTranskode/intellitranskode-helpers";
import { Language } from "@mui/icons-material";
import { AudioMixLanguageCouple } from "../../../Core/Api/Types/audio-mix-language-couple";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  type: ObjectType;
  subType: AssetSubType | null;
  countries: DbCultureInfos[];
  overrideIndex?: number;
};
export type ModalBundleAddAssetElementProps = {
  countries: DbCultureInfos[];
  component: TranskodeBundleComponent;
  xmlProfile: XmlBundleDeliveryIdentityProfile;
  onClose?: (props: CloseClbProps) => void;
};

export type ModalBundleAddAssetProps = CommonModalProps<ModalBundleAddAssetElementProps>;

const ModalBundleAddAsset = React.forwardRef<any, ModalBundleAddAssetProps>((_props, ref) => {
  const { closeModal, onClose, countries, component, xmlProfile } = _props;
  const { t } = useTranslation();
  const [type, setType] = useState<ObjectType>(
    component.type !== TranskodeBundleComponentType.Artwork ? ObjectType.Video : ObjectType.Image
  );
  const [subType, setSubType] = useState<AssetSubType | null>(null);
  const [assetIndex, setAssetIdex] = useState<number | null>(null);
  const [countriesDest, setCountriesDest] = useState<DbCultureInfos[]>([]);
  const match = xmlProfile.components.find(
    (x) => x.type === component.type && (!component.subType || x.subType === component.subType)
  );

  const MappingToDescription = (audioInfos: XmlTechAudioInfos): string => {
    let output: string = "";

    audioInfos.mappings?.forEach((mapping, indexMapping) => {
      mapping.conditions?.forEach((condition, indexCondition) => {
        let AudioMixLanguageCoupleValue: AudioMixLanguageCouple = new AudioMixLanguageCouple({
          mixEnum: condition.mixEnum,
          language:
            condition.language?.value == "DynamicReplacement"
              ? new PlatformLanguageCode({ value: "VO", label: "VO" })
              : condition.language,
          isMusicAndEffects: condition.isMusicAndEffects ?? false,
          isAudioDescription: condition.isAudioDescription ?? false,
        });

        output += GetAudioLabel(AudioMixLanguageCoupleValue, false);
        if (indexCondition < (mapping.conditions?.length ?? 0) - 1) output += " + ";
      });
      if (indexMapping < (audioInfos.mappings?.length ?? 0) - 1) output += " | ";
    });

    return output;
  };

  const handleCancelClick: React.MouseEventHandler = () => {
    closeModal();
  };

  const handleDoneClick: React.MouseEventHandler = () => {
    onClose &&
      onClose({
        type: type,
        subType: subType,
        countries: countriesDest,
        overrideIndex: assetIndex ?? match?.assets.findIndex((x) => x.type === type && x.subType === subType),
      });
    closeModal();
  };

  const isAddable = useMemo((): boolean => {
    if (!match) return false;

    // Subtitle subtitle are just UI helper (not set in Xml profile)
    let assetMatch =
      type !== ObjectType.Subtitle
        ? match.assets.find((x) => x.type === type && x.subType === subType)
        : match.assets.find((x) => x.type === type);

    if (assetIndex) assetMatch = match.assets[assetIndex];

    if (!assetMatch) return false;

    if (
      assetMatch.maxItems &&
      component.assets!.filter(
        (x) => x.type === type && x.subType === subType && (!assetIndex || x.assetIndexSource === assetIndex)
      ).length >= assetMatch.maxItems
    ) {
      return false;
    } else {
      return true;
    }
  }, [match, type, subType, component, assetIndex]);

  return (
    <Modal
      title={t("modal:title.addAnAsset")}
      reference={ref}
      closeModal={closeModal}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleDoneClick}
            disabled={
              countriesDest.length === 0 ||
              (type === ObjectType.Subtitle && !subType) ||
              (type === ObjectType.Image && !subType) ||
              !isAddable
            }
          >
            {t("button:addAsset")}
          </Button>
        </Stack>
      }
    >
      <Stack mb={3}>
        <Typography mb="6px">{t("common:assetType")}</Typography>
        <Select
          displayEmpty
          IconComponent={ExpandMoreRoundedIcon}
          value={type}
          size="small"
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          onChange={(e) => {
            let value = ObjectType[e.target.value];
            if (value !== ObjectType.Audio) {
              setAssetIdex(null);
              setSubType(null);
            }
            setType(value);
          }}
        >
          {component.type !== TranskodeBundleComponentType.Artwork && (
            <MenuItem value={ObjectType.Video}>
              <ListItemIcon>
                <AssetIcon type={ObjectType.Video} />
              </ListItemIcon>
              <ListItemText>{t("common:video")}</ListItemText>
            </MenuItem>
          )}
          {component.type !== TranskodeBundleComponentType.Artwork && (
            <MenuItem value={ObjectType.Audio}>
              <ListItemIcon>
                <AssetIcon type={ObjectType.Audio} />
              </ListItemIcon>
              <ListItemText>{t("common:audio")}</ListItemText>
            </MenuItem>
          )}
          {component.type !== TranskodeBundleComponentType.Artwork && (
            <MenuItem value={ObjectType.Subtitle}>
              <ListItemIcon>
                <AssetIcon type={ObjectType.Subtitle} />
              </ListItemIcon>
              <ListItemText>{t("field:placeholder.subtitle")}</ListItemText>
            </MenuItem>
          )}
          {component.type === TranskodeBundleComponentType.Artwork && (
            <MenuItem value={ObjectType.Image}>
              <ListItemIcon>
                <AssetIcon type={ObjectType.Image} />
              </ListItemIcon>
              <ListItemText>{t("common:image")}</ListItemText>
            </MenuItem>
          )}
        </Select>
      </Stack>
      {type === ObjectType.Audio && (match?.assets.filter((x) => x.type === ObjectType.Audio)?.length ?? 0) > 1 && (
        <Stack mb={3}>
          <Typography mb="6px">{t("modal:modalBundleAddAsset.audioAssetIndexSection.title")}</Typography>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            value={assetIndex}
            size="small"
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            onChange={(e) => {
              let index = e.target.value ? parseInt(e.target.value.toString()) : null;
              setAssetIdex(index);
              if (index == null) return;

              setSubType(match!.assets[index].subType);
            }}
          >
            {match?.assets
              .filter((x) => x.type === ObjectType.Audio)
              .map((x, index) => {
                return (
                  <MenuItem value={match?.assets.findIndex((y) => y === x)}>
                    <ListItemText
                      secondary={`Asset #${index}`}
                      primary={x.audioInfos ? MappingToDescription(x.audioInfos) : "No Data"}
                    >
                      {index}
                    </ListItemText>
                  </MenuItem>
                );
              })}
          </Select>
        </Stack>
      )}
      {(type === ObjectType.Subtitle || type === ObjectType.Image) && (
        <Stack mb={3}>
          <Typography mb="6px">{t("modal:modalBundleAddAsset.subtitleSection.title")}</Typography>
          <Select
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            value={subType}
            size="small"
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            onChange={(e) => setSubType(e.target.value ? AssetSubType[e.target.value] : null)}
          >
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.Full}>
                <ListItemText>{t("common:full")}</ListItemText>
              </MenuItem>
            )}
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.FullSdh}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.fullSDH")}</ListItemText>
              </MenuItem>
            )}
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.FullWithoutPartial}>
                <ListItemText>
                  {t("modal:modalBundleAddAsset.subtitleSection.menuItems.fullWithoutPartial")}
                </ListItemText>
              </MenuItem>
            )}
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.FullWithoutPartialSdh}>
                <ListItemText>
                  {t("modal:modalBundleAddAsset.subtitleSection.menuItems.fullWithoutPartialSDH")}
                </ListItemText>
              </MenuItem>
            )}
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.Partial}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.partial")}</ListItemText>
              </MenuItem>
            )}
            {component.type !== TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.PartialSdh}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.partialSDH")}</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkPoster169}>
                <ListItemText>{t("menu:item.poster")} 16/9</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkPoster23}>
                <ListItemText>{t("menu:item.poster")} 23</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkPosterHero}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.posterHero")}</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkPosterHorizontal}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.posterHorizontal")}</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkPosterVertical}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.posterVertical")}</ListItemText>
              </MenuItem>
            )}
            {component.type === TranskodeBundleComponentType.Artwork && (
              <MenuItem value={AssetSubType.ArtworkSquare}>
                <ListItemText>{t("modal:modalBundleAddAsset.subtitleSection.menuItems.square")}</ListItemText>
              </MenuItem>
            )}
          </Select>
        </Stack>
      )}
      <Stack>
        <Typography mb="6px">{t("modal:modalBundleAddAsset.countrySection.title")}</Typography>

        <Autocomplete
          options={countries}
          multiple
          onChange={(e, v) => {
            setCountriesDest(v);
          }}
          value={countriesDest}
          isOptionEqualToValue={(option, _value) => option.isoCodeThreeLetters === _value.isoCodeThreeLetters}
          getOptionLabel={(v) => v.englishName!}
          renderInput={(params) => (
            <OutlinedInput {...params.InputProps} inputProps={params.inputProps} required sx={{ width: "100%" }} />
          )}
          disableClearable
          fullWidth
          size="small"
        />
      </Stack>
      {!isAddable && (
        <Stack mt={3}>
          <Alert severity="warning">{t("alert:warning.maxAssetNumberReachedForType", { type })}.</Alert>
        </Stack>
      )}
    </Modal>
  );
});

export default ModalBundleAddAsset;
